/**
 * **JS 节点的心脏**：声明 → 绑定 → 执行 → 输出（纯逻辑，可单测）。
 *
 * 用户拍板的形状（`docs/js-node.md`）：
 *
 * > 可以配置固定参数，也可以配置变参行，然后解析过程中可以把参数转成
 * > 对应的变量在代码中可以读取。
 *
 * 于是这个模块只做四件事，而且**不碰图、不碰 Vue**：
 *
 * ```
 *   ① 校验声明   declarationProblems(config)      —— 只校验**输入**（输出由代码推）
 *   ② 绑定输入   bindInputs(config, 端口值)        → 代码看到的 inputs
 *   ③ 执行       new Function('inputs', code)
 *   ④ 推导输出   deriveOutputPorts(code) + 组装    —— 端口由 `return` 决定
 * ```
 *
 * ## 输出端口：**代码说了算**（2027 改，用户原话）
 *
 * > 去掉手动添加输出端口的功能，通过检查代码的 return 来判断输出端口有多少个。
 *
 * ```
 *   return { title, count }   → 两个端口：title / count
 *   return [a, b]             → 两个端口：0 / 1（下标）
 *   return someVar            → 看不出来 → **一个**「结果」端口，整个值从它出去
 *   return {}                 → 同上（空对象没有键，只能整体给）
 * ```
 *
 * ★ 为什么是**静态**扫，而不是"跑一遍看结果"：设计期不执行用户代码
 * （那是这个节点存在的底线，见下面那段安全说明）。所以端口的唯一来源
 * 只能是**读代码**，而读代码这件事必须是纯函数 —— 界面与运行期共用它。
 *
 * ⚠️ **端口的身份是下标，不是名字**（`out0` / `out1` …，见 `jsNodeType.ts`）：
 * 名字（`title` / `0`）只是标签。这样改一次代码不会把已经连好的线全拆掉
 * —— 只要那一位还在，线就还在（宿主按 handle 集合清理悬空边）。
 *
 * ## ⚠️ 安全：这里**不是沙箱**
 *
 * 用 `new Function` 跑 —— 代码能摸到 `globalThis` / `fetch` / `document` /
 * `localStorage`。**这不是安全边界，是"能跑"**。之所以接受它（用户明确选择）：
 * 先要"能写真正的 JS"，沙箱（QuickJS/WASM）以后换 —— 所以：
 *
 *   · **执行只经过本文件的 `runJsNode`**（换沙箱只改这一个函数）；
 *   · 界面与文档都**明说**这一条（不许写成"安全执行"）；
 *   · `expr` 那条真沙箱的路**继续存在**，它仍是默认。
 *
 * ⚠️ 还有两个后果必须说清楚（别让用户以为有护栏）：
 *   · **死循环会卡住整个标签页** —— 同步代码没法中断（要中断就得进 Worker/WASM）；
 *   · 代码能读能写这个页面的一切。
 *
 * ## 为什么 v1 只支持同步代码
 *
 * 要发请求就用 `tool.http` 节点（**副作用节点**，运行期异步）——
 * 让"异步"这件事只出现在 IO 节点上，纯求值器的契约就一个字节都不用动
 * （打印那条线「预览 == 打印」的根就在那个契约上，见 §2 of `js-node.md`）。
 * 所以这里遇到 `Promise` 会**明确报错**，而不是静默返回一个 Promise。
 */
import { SOCKET_TYPES, type SocketType } from '../types/socket'

/** 端口形态：固定参数（一路） / 变参行（一组 → 数组） */
export type JsPortMode = 'fixed' | 'variadic'

export interface JsPortDecl {
  /** 代码里读它用的名字：`inputs.<name>` */
  name: string
  /** 连线的类型（端口校验用） */
  type: SocketType
  mode: JsPortMode
  /** 可选说明（界面上显示） */
  hint?: string
}

/**
 * 一个**推导出来的输出端口**。
 *
 * ⚠️ `name` 是"从结果里怎么取这个值"，`label` 是"端口上显示什么"。
 * 对象键 / 下标两者一样，只有兜底的「结果」端口不同 —— 它取的是整个结果，
 * `value` 只是给这个端口一个稳定的身份。
 */
export interface JsOutputPort {
  /** 取值用的键：对象键 / 数组下标（字符串）/ `'value'`（整体） */
  name: string
  /** 端口标签（节点上显示的那个） */
  label: string
  /** field = 对象的键；index = 数组下标；whole = 整个结果 */
  kind: 'field' | 'index' | 'whole'
}

export interface JsNodeConfig {
  /** 输入声明（顺序 = 端口顺序 = 变参数组的顺序） */
  inputs: JsPortDecl[]
  /**
   * 一段函数体：`return` 一个对象 / 数组。
   *
   * ⚠️ **输出声明不在这里**了 —— 它由这段代码推出来（`deriveOutputPorts`）。
   * 多一份声明就多一个"代码和端口对不上"的机会（用户踩的就是这个）。
   */
  code: string
}

export type JsProblemKind =
  | 'name'
  | 'duplicate'
  | 'type'
  | 'reserved'
  | 'syntax'
  | 'runtime'
  | 'async'
  | 'json'

export interface JsProblem {
  kind: JsProblemKind
  /** 出问题的端口名（能定位到名字的都要给 —— 用户要的是"哪个名字错了"） */
  name?: string
  message: string
}

export interface JsRunResult {
  ok: boolean
  /**
   * **按端口顺序**的值（`out0` / `out1` …）—— 与 `deriveOutputPorts(code)`
   * 一一对应。宿主按位置接线，所以排错时看这个比看名字直接。
   */
  values: unknown[]
  /** 按端口名索引的值（对象键 / 下标 / `value`）—— 断言与日志用 */
  outputs: Record<string, unknown>
  /** 代码 `return` 的原始值（排错用；成功时也在） */
  raw?: unknown
  problems: JsProblem[]
}

/**
 * 名字必须是**合法标识符**（因为代码里就是 `inputs.<name>`）。
 *
 * ⚠️ 这条校验很值钱：不校验的话，用户写一个 `my-field` 或 `2rows`，
 * 代码里 `inputs.my-field` 会静默变成 `undefined`（JS 里这是减法 ✗），
 * 查起来是天坑。
 */
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/

/**
 * 保留名：代码里已经占用的东西。
 *
 * `inputs` 自己不用列（它是整个参数对象）；`returns` 保留给以后的
 * "多返回值"写法（现在只认 `return`，但先占住名字免得以后改名）。
 */
const RESERVED = new Set(['inputs', 'returns', 'globalThis', 'window', 'document', 'fetch'])

/**
 * 最多长出几路输出端口。
 *
 * 超了就退回**一个**「结果」端口：一个 `return` 里塞 40 个键的节点，
 * 画布上那一列端口比屏幕还长，而整份结果本来就能从一个端口出去。
 */
const MAX_OUTPUT_PORTS = 32

/** 校验一份输入声明（增删参数、改名字、改类型时都要过这一关） */
export function declarationProblems(config: JsNodeConfig): JsProblem[] {
  const problems: JsProblem[] = []
  const seen = new Set<string>()
  for (const decl of config.inputs) {
    if (!decl.name || !IDENTIFIER.test(decl.name)) {
      problems.push({
        kind: 'name',
        name: decl.name,
        /*
         * ⚠️ 文案里**不用反引号 / 星号**这类 markdown：它要显示在面板的
         * 一段纯文本里（那里不解析 markdown，会原样看到一个反引号）。
         */
        message: `输入「${decl.name || '（空）'}」不是合法的名字 —— 代码里要用 inputs.${decl.name || '名字'} 读它，只能是字母 / 下划线 / 数字（不能有横线、不能以数字开头）`,
      })
    } else if (RESERVED.has(decl.name)) {
      problems.push({
        kind: 'reserved',
        name: decl.name,
        message: `输入「${decl.name}」是保留名（代码里已经占用了这个变量），换一个`,
      })
    }
    if (seen.has(decl.name)) {
      problems.push({ kind: 'duplicate', name: decl.name, message: `「${decl.name}」声明了两次` })
    }
    seen.add(decl.name)
    if (!SOCKET_TYPES.includes(decl.type)) {
      problems.push({ kind: 'type', name: decl.name, message: `「${decl.name}」的类型「${decl.type}」不认识` })
    }
  }
  /*
   * ⚠️ **输出侧不校验**：端口不是用户声明的，是从 `return` 推出来的
   * （键可以是 `my-key`、`0`、中文 —— 它们只是取值的键，不是标识符）。
   */
  return problems
}

/**
 * **绑定**：把图上收到的端口值，转成代码里读到的 `inputs`。
 *
 * | 形态 | 输入 | 代码里 |
 * |---|---|---|
 * | 固定 | 一个值 | 那个值（没连就是 `undefined`） |
 * | 变参 | 一组值（可能带空槽） | **数组**（空槽丢掉，顺序 = 端口顺序） |
 *
 * ⚠️ **变参在代码里是数组**，这是整个设计里最省心的一条：
 * 用户不需要知道"端口"那套东西，只要会 `inputs.rows.map(...)`。
 *
 * ⚠️ 空槽（连了 1 路、但声明里还留着待接的位置）要**丢掉**：
 * 组节点那边踩过同一个坑（`node-packages.md` §3.5 空槽纪律）——
 * 不丢的话数组里会多出 `undefined`，用户的 `map` 就多跑一轮。
 */
export function bindInputs(
  config: JsNodeConfig,
  values: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {}
  for (const decl of config.inputs) {
    if (decl.mode === 'fixed') {
      inputs[decl.name] = values[decl.name]
      continue
    }
    const raw = values[decl.name]
    const list = Array.isArray(raw) ? raw : raw === undefined ? [] : [raw]
    inputs[decl.name] = list.filter((item) => item !== undefined && item !== null)
  }
  return inputs
}

/* ══════════════════════════════════════════════════════════════════
   ① 读代码：`return` → 输出端口
   ══════════════════════════════════════════════════════════════════

   下面这一小段是**手写的 JS 扫描器**，只干一件事：把顶层 `return` 后面
   那个字面量认出来。为什么不用 acorn / 一个真解析器：

     · 内核**没有** JS 解析器依赖（`acorn` 只是 vue 编译器的传递依赖，
       不能当运行时的底盘 —— 它随时可能从依赖树里消失）；
     · 需要的语义极小：**对象字面量的顶层键** / **数组字面量的元素个数**。
       别的（标识符、调用、三元）一律"看不出来"，退回一个端口就完事。

   ⚠️ 所以这里的态度是：**认得出来就长端口，认不出来就诚实地说认不出来**。
   绝不猜 —— 猜错的后果是端口和代码对不上（正是这次要修的东西）。
*/

/** 扫出来的东西：顶层 `return` 的表达式源码 */
interface ScanResult {
  /** 顶层 `return` 后面那段表达式的源码 */
  expressions: string[]
  /** 见过裸 `return`（`return;` / `return` 结尾）—— 它给不出任何键 */
  bare: boolean
}

/*
 * ⚠️ 标识符用 **Unicode** 那一套（`\p{ID_Start}` / `\p{ID_Continue}`），
 * 不是 `[A-Za-z_$]`：这个工具的用户是中文用户，`return { 标题: … }`
 * 是最自然的写法之一。按 ASCII 认的话它会掉进"看不出来"，
 * 于是端口变成一个「结果」—— 而代码明明写得清清楚楚。
 */
const IDENT_START = /[$_\p{ID_Start}]/u
const IDENT_PART = /[$\u200c\u200d\p{ID_Continue}]/u

/**
 * 上一个有意义的字符能不能接一个**正则字面量**。
 *
 * 这是 `/` 的二义性（除号 vs 正则）在扫描器里的唯一处理点：
 * 前面是值（标识符、`)`、`]`）就是除号，否则是正则。
 */
function regexAllowed(prev: string): boolean {
  if (prev === '') return true
  return !(/[)\]]/.test(prev) || IDENT_PART.test(prev))
}

/** 跳过一段引号字符串（`start` 在引号上），返回结束后的位置 */
function skipQuoted(code: string, start: number): number {
  const quote = code[start]
  let i = start + 1
  while (i < code.length) {
    const ch = code[i]
    if (ch === '\\') {
      i += 2
      continue
    }
    if (ch === quote) return i + 1
    /* 未闭合：当它到行尾结束（写代码写到一半是常态，扫描器不能卡住） */
    if (ch === '\n' && quote !== '`') return i
    i += 1
  }
  return i
}

/** 跳过一段正则字面量（`start` 在 `/` 上） */
function skipRegex(code: string, start: number): number {
  let i = start + 1
  let inClass = false
  while (i < code.length) {
    const ch = code[i]
    if (ch === '\\') {
      i += 2
      continue
    }
    if (ch === '\n') return i /* 未闭合 */
    if (ch === '[') inClass = true
    else if (ch === ']') inClass = false
    else if (ch === '/' && !inClass) {
      i += 1
      while (i < code.length && /[a-z]/.test(code[i])) i += 1
      return i
    }
    i += 1
  }
  return i
}

/** 跳过一段模板字面量（`start` 在反引号上），`${…}` 里递归跳过 */
function skipTemplate(code: string, start: number): number {
  let i = start + 1
  while (i < code.length) {
    const ch = code[i]
    if (ch === '\\') {
      i += 2
      continue
    }
    if (ch === '`') return i + 1
    if (ch === '$' && code[i + 1] === '{') {
      i = skipBalancedFrom(code, i + 1)
      continue
    }
    i += 1
  }
  return i
}

/**
 * 从 `{` / `[` / `(` 开始跳过一整个配对的括号块
 * （含内部的字符串、模板、注释、正则）。返回闭合括号**之后**的位置。
 */
function skipBalancedFrom(code: string, start: number): number {
  const open = code[start]
  const close = open === '{' ? '}' : open === '[' ? ']' : ')'
  let depth = 0
  let i = start
  let prev = ''
  while (i < code.length) {
    const ch = code[i]
    if (ch === '/' && code[i + 1] === '/') {
      const nl = code.indexOf('\n', i)
      i = nl < 0 ? code.length : nl + 1
      continue
    }
    if (ch === '/' && code[i + 1] === '*') {
      const end = code.indexOf('*/', i + 2)
      i = end < 0 ? code.length : end + 2
      continue
    }
    if (ch === '"' || ch === "'") {
      i = skipQuoted(code, i)
      prev = 'x'
      continue
    }
    if (ch === '`') {
      i = skipTemplate(code, i)
      prev = 'x'
      continue
    }
    if (ch === '/' && regexAllowed(prev)) {
      i = skipRegex(code, i)
      prev = 'x'
      continue
    }
    if (ch === open && depth === 0) depth = 1
    else if (ch === open) depth += 1
    else if (ch === close) {
      depth -= 1
      if (depth <= 0) return i + 1
    }
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') prev = ch
    i += 1
  }
  return i
}

/**
 * 从某个位置开始，跳到 `return` 那段表达式之后（分号 / 本层 `}` / 结尾）。
 *
 * ⚠️ 允许**多抓一点**（ASI 换行后面的语句也会被吞进来）：下游只认开头
 * 那个字面量，多抓的部分不影响判断；而少抓会把对象截断（那才会判断错）。
 *
 * ⚠️⚠️ 但**下一个顶层 `return` 必须停下来**：不停的话
 * ```
 *   if (a) return { x: 1 }
 *   return [1, 2]
 * ```
 * 会被当成**一个** return（吞掉了第二个），于是"两个分支的形状对不上"
 * 这件事根本没人发现 —— 套件里那条"一半对象一半数组 → 退回整体"就是
 * 这么抓出来的。
 *
 * 判据用"紧跟其后的不是 `:`"来区分语句和属性名（`return { return: 1 }`
 * 里的那个 `return` 是键，不是语句）。
 */
function scanExpressionEnd(code: string, start: number): number {
  let i = start
  let depth = 0
  let prev = ''
  while (i < code.length) {
    const ch = code[i]
    if (ch === '/' && code[i + 1] === '/') {
      const nl = code.indexOf('\n', i)
      i = nl < 0 ? code.length : nl + 1
      continue
    }
    if (ch === '/' && code[i + 1] === '*') {
      const end = code.indexOf('*/', i + 2)
      i = end < 0 ? code.length : end + 2
      continue
    }
    if (ch === '"' || ch === "'") {
      i = skipQuoted(code, i)
      prev = 'x'
      continue
    }
    if (ch === '`') {
      i = skipTemplate(code, i)
      prev = 'x'
      continue
    }
    if (ch === '/' && regexAllowed(prev)) {
      i = skipRegex(code, i)
      prev = 'x'
      continue
    }
    if (depth === 0 && IDENT_START.test(ch)) {
      let j = i
      while (j < code.length && IDENT_PART.test(code[j])) j += 1
      if (code.slice(i, j) === 'return') {
        let k = j
        while (k < code.length && /\s/.test(code[k])) k += 1
        if (code[k] !== ':') return i
      }
      prev = code[j - 1]
      i = j
      continue
    }
    if (ch === '{' || ch === '[' || ch === '(') depth += 1
    else if (ch === '}' || ch === ']' || ch === ')') {
      /* depth 0 上的闭括号 = 本层块结束（对象字面量自己的 `}` 已被上面的 depth 吃掉） */
      if (depth === 0) return i
      depth -= 1
    } else if (ch === ';' && depth === 0) return i
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') prev = ch
    i += 1
  }
  return i
}

/**
 * 扫出所有**顶层** `return` 的表达式源码。
 *
 * 「顶层」= 不在 `function` / `=>` 的函数体里（`rows.map(row => { return … })`
 * 里的那个 `return` 描述的是**元素**，不是这个节点的输出）。
 */
function scanReturns(code: string): ScanResult {
  const expressions: string[] = []
  let bare = false
  let i = 0
  /** 未闭合的 `{`：true = 它是**函数体**（`function f() {` / `=> {`） */
  const braceIsFunction: boolean[] = []
  let functionDepth = 0
  let functionKeyword = false
  let arrowPending = false
  let prev = ''

  while (i < code.length) {
    const ch = code[i]

    if (ch === '/' && code[i + 1] === '/') {
      const nl = code.indexOf('\n', i)
      i = nl < 0 ? code.length : nl + 1
      continue
    }
    if (ch === '/' && code[i + 1] === '*') {
      const end = code.indexOf('*/', i + 2)
      i = end < 0 ? code.length : end + 2
      continue
    }
    if (ch === '"' || ch === "'") {
      i = skipQuoted(code, i)
      prev = 'x'
      continue
    }
    if (ch === '`') {
      i = skipTemplate(code, i)
      prev = 'x'
      continue
    }
    if (ch === '/' && regexAllowed(prev)) {
      i = skipRegex(code, i)
      prev = 'x'
      continue
    }
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1
      continue
    }

    /* 箭头函数：只有**紧跟**的那个 `{` 才是函数体（`=> ({…})` 不是） */
    const arrowBody = arrowPending && ch === '{'
    arrowPending = false

    if (IDENT_START.test(ch)) {
      const start = i
      while (i < code.length && IDENT_PART.test(code[i])) i += 1
      const word = code.slice(start, i)
      if (word === 'function') functionKeyword = true
      if (word === 'return' && functionDepth === 0) {
        /* 跳过 `return` 与表达式之间的空白和注释 */
        let j = i
        for (;;) {
          while (j < code.length && /\s/.test(code[j])) j += 1
          if (code[j] === '/' && code[j + 1] === '/') {
            const nl = code.indexOf('\n', j)
            j = nl < 0 ? code.length : nl + 1
            continue
          }
          if (code[j] === '/' && code[j + 1] === '*') {
            const end = code.indexOf('*/', j + 2)
            j = end < 0 ? code.length : end + 2
            continue
          }
          break
        }
        /*
         * ⚠️ 紧跟一个 `:` 的话，这不是 `return` 语句，而是**一个叫 return
         * 的属性名**（`{ return: 1 }` 在 ES5+ 里是合法的）。不排掉它的话，
         * 这一处会被当成"看不出来"的 return，把整份输出的端口全降级成
         * 一个「结果」。
         */
        if (code[j] === ':') {
          prev = word[word.length - 1] ?? ''
          continue
        }
        if (j >= code.length || code[j] === ';' || code[j] === '}') {
          bare = true
        } else {
          const end = scanExpressionEnd(code, j)
          expressions.push(code.slice(j, end).trim())
          i = end
          prev = ')'
          continue
        }
      }
      prev = word[word.length - 1] ?? ''
      continue
    }

    if (ch === '{') {
      const isFunction = functionKeyword || arrowBody
      functionKeyword = false
      braceIsFunction.push(isFunction)
      if (isFunction) functionDepth += 1
      prev = ch
      i += 1
      continue
    }
    if (ch === '}') {
      if (braceIsFunction.pop()) functionDepth -= 1
      functionKeyword = false
      prev = ch
      i += 1
      continue
    }
    if (ch === '=' && code[i + 1] === '>') {
      arrowPending = true
      prev = '>'
      i += 2
      continue
    }

    if (ch === ';') functionKeyword = false
    prev = ch
    i += 1
  }

  return { expressions, bare }
}

/**
 * 按**顶层逗号**切开（括号 / 字符串 / 模板 / 注释 / 正则里的逗号不算）。
 *
 * 对象键、数组元素都靠它 —— 写错一处就会把 `{ a: f(1, 2) }` 认成两个键。
 */
function splitTopLevel(inner: string): string[] {
  const parts: string[] = []
  let start = 0
  let depth = 0
  let i = 0
  let prev = ''
  while (i < inner.length) {
    const ch = inner[i]
    if (ch === '"' || ch === "'") {
      i = skipQuoted(inner, i)
      prev = 'x'
      continue
    }
    if (ch === '`') {
      i = skipTemplate(inner, i)
      prev = 'x'
      continue
    }
    if (ch === '/' && inner[i + 1] === '/') {
      const nl = inner.indexOf('\n', i)
      i = nl < 0 ? inner.length : nl + 1
      continue
    }
    if (ch === '/' && inner[i + 1] === '*') {
      const end = inner.indexOf('*/', i + 2)
      i = end < 0 ? inner.length : end + 2
      continue
    }
    if (ch === '/' && regexAllowed(prev)) {
      i = skipRegex(inner, i)
      prev = 'x'
      continue
    }
    if (ch === '{' || ch === '[' || ch === '(') depth += 1
    else if (ch === '}' || ch === ']' || ch === ')') depth -= 1
    else if (ch === ',' && depth === 0) {
      parts.push(inner.slice(start, i))
      start = i + 1
    }
    if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') prev = ch
    i += 1
  }
  parts.push(inner.slice(start))
  return parts
}

/** 一段字面量的**闭合位置之后**（`start` 在开括号上）；不成对返回 -1 */
function matchingBracket(src: string, start: number): number {
  const close = src[start] === '{' ? '}' : src[start] === '[' ? ']' : ')'
  const end = skipBalancedFrom(src, start)
  return end > 0 && src[end - 1] === close ? end : -1
}

/**
 * 对象字面量的**顶层键**（按出现顺序，去重）。
 *
 * 返回 `null` = 看不出来（有展开 `...x`、计算键 `[k]`、写法怪），
 * 调用方据此退回一个「结果」端口 —— **不猜**。
 */
function objectKeys(inner: string): string[] | null {
  const keys: string[] = []
  for (const part of splitTopLevel(inner)) {
    const text = part.trim()
    if (!text) continue
    if (text.startsWith('...')) return null
    const key = keyOf(text)
    if (key === null) return null
    if (!keys.includes(key)) keys.push(key)
  }
  return keys
}

/** 一个属性项（`a: 1` / `a` / `'a-b': 1` / `get a() {}`）的键 */
function keyOf(text: string): string | null {
  const first = text[0]
  if (first === '"' || first === "'") {
    const end = skipQuoted(text, 0)
    if (text[end - 1] !== first) return null
    return text.slice(1, end - 1).replace(/\\(['"\\])/g, '$1')
  }
  if (first >= '0' && first <= '9') {
    const match = /^[0-9]+(?:\.[0-9]+)?/.exec(text)
    return match ? match[0] : null
  }
  if (first === '[') return null /* 计算键 */
  if (!IDENT_START.test(first)) return null

  let end = 0
  while (end < text.length && IDENT_PART.test(text[end])) end += 1
  let word = text.slice(0, end)
  let rest = text.slice(end).trimStart()
  /*
   * `get a() {}` / `set a(v) {}` / `async a() {}` —— 真正的键在后面那个词上。
   * 不认这三种的话，端口上会出现一个叫 `get` 的键（而结果里根本没有它）。
   */
  if (
    (word === 'get' || word === 'set' || word === 'async') &&
    rest &&
    IDENT_START.test(rest[0]) &&
    !rest.startsWith(':')
  ) {
    const match = /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(rest)
    if (match) {
      word = match[0]
      rest = rest.slice(match[0].length).trimStart()
    }
  }
  return word
}

/** 数组字面量的元素个数；有展开 / 看不出来返回 null */
function arrayLength(inner: string): number | null {
  let count = 0
  for (const part of splitTopLevel(inner)) {
    const text = part.trim()
    if (!text) continue
    if (text.startsWith('...')) return null
    count += 1
  }
  return count
}

/** 一个 `return` 的表达式推出来的形状 */
interface ReturnShape {
  keys?: string[]
  length?: number
}

/** 认一个表达式：对象字面量 → 键；数组字面量 → 元素个数；别的 → null */
function shapeOf(expression: string): ReturnShape | null {
  const text = expression.trimStart()
  if (text.startsWith('{')) {
    const end = matchingBracket(text, 0)
    if (end < 0) return null
    const keys = objectKeys(text.slice(1, end - 1))
    return keys ? { keys } : null
  }
  if (text.startsWith('[')) {
    const end = matchingBracket(text, 0)
    if (end < 0) return null
    const length = arrayLength(text.slice(1, end - 1))
    return length === null ? null : { length }
  }
  return null
}

/**
 * ★★ **输出端口 = 代码里 `return` 的形状**（这个模块对外的第二张脸）。
 *
 * ```
 *   return { title, count }   → title / count
 *   return [a, b, c]          → 0 / 1 / 2
 *   多个 return（分支）        → 键的**并集**（按首次出现顺序）
 *   看不出来 / 空对象 / 太多    → 一个「结果」端口（整个值）
 * ```
 *
 * ⚠️ 永远**至少一个**端口：一个输出都没有的节点在图上没法用
 * （"我只想让它算一个整体结果"是最常见的用法）。
 */
export function deriveOutputPorts(code: string): JsOutputPort[] {
  const whole: JsOutputPort[] = [{ name: 'value', label: '结果', kind: 'whole' }]
  const { expressions } = scanReturns(code)
  if (expressions.length === 0) return whole

  const shapes = expressions.map(shapeOf)
  if (shapes.some((shape) => shape === null)) return whole
  const list = shapes as ReturnShape[]

  if (list.every((shape) => shape.keys)) {
    const keys: string[] = []
    for (const shape of list) {
      for (const key of shape.keys ?? []) if (!keys.includes(key)) keys.push(key)
    }
    if (keys.length === 0 || keys.length > MAX_OUTPUT_PORTS) return whole
    return keys.map((key) => ({ name: key, label: key, kind: 'field' as const }))
  }

  if (list.every((shape) => shape.length !== undefined)) {
    const longest = Math.max(...list.map((shape) => shape.length ?? 0))
    if (longest === 0 || longest > MAX_OUTPUT_PORTS) return whole
    return Array.from({ length: longest }, (_, index) => ({
      name: String(index),
      label: String(index),
      kind: 'index' as const,
    }))
  }

  /* 一半分支给对象、一半给数组 —— 静态没法统一，退回整体 */
  return whole
}

/**
 * **结果的形状归一化**（用户原话）：
 *
 * > 这个结果经 JSON 序列化后，确保是一个对象或者一个数组。
 *
 * ```
 *   对象 / 数组   → 原样（但过一遍 JSON，保证下游拿到的是**纯数据**）
 *   标量          → 包一层 `{ value: 标量 }`
 *                   （用户选的：宽容一点，`return inputs.text` 也能跑）
 *   null / 空     → `{}`
 * ```
 *
 * ⚠️ 为什么要真的 `JSON.stringify` 一遍，而不是"看着像对象就放行"：
 * 下游（列表函数 / 打印 / 导出）拿到的必须是**可序列化的数据**。一个
 * `Date`、一个类实例、一个带循环引用的对象，等到了那一层才炸的话，
 * 报错离现场已经很远（表现是"导出的模板是空的"）。这里当场说清。
 */
export function normalizeJsResult(
  raw: unknown,
): { ok: true; data: unknown } | { ok: false; message: string } {
  let shaped: unknown
  if (raw === undefined || raw === null) shaped = {}
  else if (typeof raw === 'object') shaped = raw
  else shaped = { value: raw }

  try {
    const text = JSON.stringify(shaped)
    if (text === undefined) return { ok: true, data: {} }
    const data: unknown = JSON.parse(text)
    /*
     * ⚠️ 还要再看一眼结果：`Date` / `Symbol` 这类东西过了 JSON **会变成
     * 标量或空壳**（`new Date()` → 一个字符串）。用户要的是"确保是一个
     * 对象或者一个数组"，所以这里再兜一次底。
     */
    if (data === null || typeof data !== 'object') return { ok: true, data: { value: data } }
    return { ok: true, data }
  } catch (error) {
    return {
      ok: false,
      message:
        `返回值没法 JSON 序列化（${(error as Error).message}）—— ` +
        'JS 节点的输出必须是纯数据：对象 / 数组 / 字符串 / 数字 / 布尔',
    }
  }
}

/**
 * **执行 + 组装输出**。
 *
 * 顺序：校验输入声明 → 跑代码 → **归一化结果**（对象或数组）→ 按
 * `deriveOutputPorts(code)` 取每一个端口的值。
 *
 * ⚠️ 端口是**在这里再推一遍**的，而不是从调用方接一份声明：那样才不可能
 * 出现"端口表说 2 个、代码给 3 个"这种对不上的状态（用户踩的就是它）。
 */
export function runJsNode(
  config: JsNodeConfig,
  values: Readonly<Record<string, unknown>>,
): JsRunResult {
  const problems = declarationProblems(config)
  if (problems.length > 0) {
    /* 声明都不合法就别执行了 —— 那时读到的名字必然是错的 */
    return { ok: false, values: [], outputs: {}, problems }
  }

  const inputs = bindInputs(config, values)
  let fn: (inputs: Record<string, unknown>) => unknown
  try {
    /*
     * ⚠️ 只注入 `inputs` 一个参数（不用 `with`，不做作用域拼接）：
     * 这不是为了安全（它不是安全边界），而是为了**报错清楚** ——
     * 用户写错名字时得到的是"inputs.xxx is undefined"，
     * 而不是"某个我们拼进去的怪变量"。
     *
     * ⚠️ `new Function` 的 body 是**函数体**：代码里写 `return { … }`。
     */
    fn = new Function('inputs', `"use strict";\n${config.code}`) as typeof fn
  } catch (error) {
    return {
      ok: false,
      values: [],
      outputs: {},
      problems: [{ kind: 'syntax', message: `代码有语法错误：${(error as Error).message}` }],
    }
  }

  let raw: unknown
  try {
    raw = fn(inputs)
  } catch (error) {
    return {
      ok: false,
      values: [],
      outputs: {},
      problems: [{ kind: 'runtime', message: `运行时出错：${(error as Error).message}` }],
    }
  }

  if (raw && typeof (raw as { then?: unknown }).then === 'function') {
    /*
     * ⚠️ 必须**当场报错**而不是把 Promise 传下去：
     * 传下去的话，下游拿到的是一个 Promise 对象，它会一路"看起来有值"，
     * 直到某一层把它当字符串拼出 "[object Promise]"。
     * 要发请求请用 `tool.http` 节点（副作用节点，异步归运行期）。
     */
    return {
      ok: false,
      values: [],
      outputs: {},
      problems: [
        {
          kind: 'async',
          /*
           * ⚠️ 文案里**不许点名具体节点类型**（比如"用 tool.http"）——
           * 那是包的领域知识，内核不该认识它（`verify:boundary` 会当场报，
           * 而且报得对：内核一旦点名，就是在替某个包做广告）。
           * 说"哪一类节点"就够了。
           */
          message:
            '这段代码返回了 Promise（用了 await / async）。JS 节点只支持同步代码 —— ' +
            '网络这类异步操作请交给**副作用节点**（它们在运行期执行，可以把结果接进 inputs）。',
        },
      ],
    }
  }

  const normalized = normalizeJsResult(raw)
  if (!normalized.ok) {
    /*
     * ⚠️ 这条路上**不带 `raw`**：走到这里最常见的原因就是"结果里有循环引用"，
     * 而那份 `raw` 会被运行记录一路带进界面 —— 一个循环对象足以让
     * 展示那一层再炸一次（报错离现场更远）。
     */
    return {
      ok: false,
      values: [],
      outputs: {},
      problems: [{ kind: 'json', message: normalized.message }],
    }
  }

  const ports = deriveOutputPorts(config.code)
  const data = normalized.data as Record<string, unknown>
  const outputs: Record<string, unknown> = {}
  const list: unknown[] = []
  for (const port of ports) {
    if (port.kind === 'whole') {
      outputs[port.name] = normalized.data
      list.push(normalized.data)
      continue
    }
    const value = data[port.name]
    outputs[port.name] = value
    list.push(value)
    /*
     * ⚠️⚠️ **`undefined` 是合法的值，不是错误**（2027 改，用户报的第 2 个问题）。
     *
     * 用户原话：「我给 JS 代码节点添加了一个输入数值，添加了一个输入布尔，
     * 然后输出连接到调试输出节点上没有任何输出，也没有任何报错」——
     * 那条链其实**报错了**，只是报在一个他没看的地方：他声明了布尔那一路输入
     * 但没接线，于是 `return { 值: inputs.in0, 开关: inputs.in1 }` 里的
     * `开关` 是 `undefined`，而这个函数把它判成了"代码没有返回输出「开关」"。
     *
     * 那条判据是**上一条契约**留下的：端口由用户手填一张声明表，代码没给
     * 某个键就说明"声明和代码对不上"，当场报错是对的。现在端口是**从代码推**
     * 的 —— 代码明明白白写了 `开关` 这个键，它只是这次没有值（输入没接、
     * 或者某个分支没走到）。这和"上游端口没接"是同一件事，
     * 而后者在整个系统里从来不算错误（下游会回退到自己的内联默认值）。
     *
     * 所以：**没有值就是没有值**，如实传 `undefined` 下去，让下游按老规矩处理。
     */
  }

  return { ok: true, values: list, outputs, raw, problems: [] }
}
